import { 
  Pipeline, 
  Step, 
  StepPosition, 
  PipelineLayoutConfig, 
  PipelineTimeMarker, 
  MilestoneWithLevel, 
  MilestoneLayoutConfig, 
  ProcessedMilestone
} from '../types';

// Layout and sizing constants
const LAYOUT_CONSTANTS = {
  MIN_WIDTH_PER_PIPELINE: 300,
  MIN_TOTAL_WIDTH: 1200,
  MIN_STEP_WIDTH: 200,
  MARGIN_SIZE: 20,
  LANE_HEIGHT: 60,
  LANE_PADDING: 10,
  DEFAULT_MIN_STEP_WIDTH: 75,
  STEP_WIDTH_OFFSET: 0.01,
  MIN_STEP_WIDTH_PERCENTAGE: 25,
  PIPELINE_WIDTH_RATIO: 0.8,
  TIME_MARKER_OFFSET: 0.005, // 0.5% offset for time markers
  OVERLAP_THRESHOLD: 3, // 3% of timeline width for milestone overlap
  LEVEL_SPACING: 35, // pixels between milestone levels
  BASE_TOP_OFFSET: 10, // base top offset for milestones
  MIN_MILESTONE_HEIGHT: 80, // minimum milestone container height
  BASE_Z_INDEX: 10 // base z-index for milestones
} as const;

// Export the minimum step width percentage for use in components
export const MIN_STEP_WIDTH_PERCENTAGE = LAYOUT_CONSTANTS.MIN_STEP_WIDTH_PERCENTAGE;

// Time conversion constants
const TIME_CONSTANTS = {
  ONE_HOUR: 1000 * 60 * 60,
  ONE_DAY: 24 * 60 * 60 * 1000
} as const;
/**
 * Creates the layout configuration for pipeline rendering.
 * Calculates dimensions and spacing based on the number of pipelines to display.
 * 
 * @param pipelineCount - Number of pipelines to be displayed
 * @returns Configuration object with layout dimensions and spacing
 */
export const createPipelineLayoutConfig = (pipelineCount: number): PipelineLayoutConfig => {
  const totalMinWidth = Math.max(
    pipelineCount * LAYOUT_CONSTANTS.MIN_WIDTH_PER_PIPELINE, 
    LAYOUT_CONSTANTS.MIN_TOTAL_WIDTH
  );
  
  return {
    totalMinWidth,
    columnWidth: totalMinWidth / pipelineCount,
    minStepWidth: LAYOUT_CONSTANTS.MIN_STEP_WIDTH,
    marginSize: LAYOUT_CONSTANTS.MARGIN_SIZE,
    laneHeight: LAYOUT_CONSTANTS.LANE_HEIGHT,
    lanePadding: LAYOUT_CONSTANTS.LANE_PADDING,
  };
};
/**
 * Calculates the pixel-based position for a step based on pipeline start and end positions.
 * Determines step positioning using timing calculations when available, with fallback to
 * proportional positioning within pipeline columns.
 * 
 * @param step - Step object with timing and lane information
 * @param pipeline - Pipeline object containing the step
 * @param config - Layout configuration with dimensions and spacing
 * @returns Position object with left, width, and top coordinates
 */
export const calculateStepPosition = (
  step: Step,
  pipeline: Pipeline,
  config: PipelineLayoutConfig
): StepPosition => {
  const { laneHeight, lanePadding } = config;
  
  let stepLeft: number = 0;
  let stepWidth: number = LAYOUT_CONSTANTS.DEFAULT_MIN_STEP_WIDTH;
  
  // Calculate step position based on pipeline start and end positions
  if (pipeline.startPosition !== undefined && pipeline.endPosition !== undefined && 
      step.startTime && step.endTime && pipeline.startTime && pipeline.endTime) {
    
    const pipelineStartTime = pipeline.startTime.getTime();
    const pipelineEndTime = pipeline.endTime.getTime();
    const pipelineDuration = pipelineEndTime - pipelineStartTime;
    
    const stepStartTime = step.startTime.getTime();
    const stepEndTime = step.endTime.getTime();
    const stepDuration = stepEndTime - stepStartTime;
    
    if (pipelineDuration > 0) {
      // Calculate step's relative position within the pipeline (0-1)
      const stepStartRatio = (stepStartTime - pipelineStartTime) / pipelineDuration;
      const stepDurationRatio = stepDuration / pipelineDuration;
            
      // Calculate step position as percentage of total timeline
      stepLeft = stepStartRatio * 100;
      stepWidth = stepDurationRatio * 100;
    }
  } else if (pipeline.startPosition !== undefined && pipeline.endPosition !== undefined) {
    // Fallback: use pipeline positions without timing calculations
    const pipelineWidth = pipeline.endPosition - pipeline.startPosition;
    stepLeft = pipeline.startPosition;
    stepWidth = Math.max(
      pipelineWidth * LAYOUT_CONSTANTS.PIPELINE_WIDTH_RATIO, 
      (LAYOUT_CONSTANTS.DEFAULT_MIN_STEP_WIDTH / LAYOUT_CONSTANTS.MIN_TOTAL_WIDTH) * 100
    );
  }
  return {
    left: stepLeft, // Return as percentage
    width: stepWidth, // Return as percentage
    top: step.lane * laneHeight + lanePadding,
  };
};
/**
 * Calculates the required height for a pipeline column based on its steps.
 * Determines the vertical space needed to display all step lanes with proper spacing.
 * 
 * @param pipeline - Pipeline object containing steps with lane assignments
 * @param config - Layout configuration with lane height and padding
 * @returns Total height in pixels required for the pipeline column
 */
export const calculatePipelineColumnHeight = (
  pipeline: Pipeline,
  config: PipelineLayoutConfig
): number => {
  const maxLane = Math.max(...pipeline.steps.map(s => s.lane), 0);
  return (maxLane + 1) * config.laneHeight + config.lanePadding;
};
/**
 * Calculates step duration in hours for display.
 * Returns the duration between step start and end times in hours, rounded to nearest hour.
 * 
 * @param step - Step object with startTime and endTime properties
 * @returns Duration in hours, or null if times are not available
 */
export const calculateStepDuration = (startTime: number|null, endTime: number|null): string => {
  if (!startTime) {
    return '';
  }

  const calculatedEndTime = !endTime ? new Date().getTime() : endTime; 

  return (calculatedEndTime - startTime) <= TIME_CONSTANTS.ONE_DAY ? 
        `${((calculatedEndTime - startTime) / TIME_CONSTANTS.ONE_HOUR).toFixed(1)} hours`
        : `${((calculatedEndTime - startTime) / TIME_CONSTANTS.ONE_DAY).toFixed(1)} days`;
};
/**
 * Generates time markers for pipelines with start/end positions.
 * Creates markers at the beginning and end of each pipeline column with slight offsets.
 * 
 * @param pipelines - Array of pipeline objects to generate markers for
 * @returns Array of time markers with position and timing information
 */
export const generatePipelineTimeMarkers = (pipelines: Pipeline[]): PipelineTimeMarker[] => {
  const markers: PipelineTimeMarker[] = [];
  
  pipelines.forEach((pipeline, index) => {
    const pipelineWidth = 100 / pipelines.length;
    const startPosition = index * pipelineWidth;
    const endPosition = (index + 1) * pipelineWidth;
    
    // Add start marker
    if (pipeline.startTime) {
      markers.push({
        pipeline,
        type: 'start',
        time: pipeline.startTime,
        position: startPosition + (pipelineWidth * LAYOUT_CONSTANTS.TIME_MARKER_OFFSET)
      });
    }
    
    // Add end marker
    if (pipeline.endTime) {
      markers.push({
        pipeline,
        type: 'end',
        time: pipeline.endTime,
        position: endPosition - (pipelineWidth * LAYOUT_CONSTANTS.TIME_MARKER_OFFSET)
      });
    }
  });
  
  return markers;
};
/**
 * Calculates the minimum width required for pipeline timeline.
 * Ensures adequate space for displaying multiple pipelines with proper spacing.
 * 
 * @param pipelineCount - Number of pipelines to be displayed
 * @returns Minimum width in pixels for the timeline container
 */
export const calculateTimelineMinWidth = (pipelineCount: number): number => {
  return Math.max(
    pipelineCount * LAYOUT_CONSTANTS.MIN_WIDTH_PER_PIPELINE, 
    LAYOUT_CONSTANTS.MIN_TOTAL_WIDTH
  );
};
/**
 * Creates milestone layout configuration with default values.
 * Provides configuration for milestone positioning and overlap detection.
 * 
 * @returns Configuration object with milestone layout parameters
 */
export const createMilestoneLayoutConfig = (): MilestoneLayoutConfig => ({
  overlapThreshold: LAYOUT_CONSTANTS.OVERLAP_THRESHOLD,
  levelSpacing: LAYOUT_CONSTANTS.LEVEL_SPACING,
  baseTopOffset: LAYOUT_CONSTANTS.BASE_TOP_OFFSET,
  minHeight: LAYOUT_CONSTANTS.MIN_MILESTONE_HEIGHT,
});
/**
 * Detects overlapping milestones and assigns vertical levels to prevent visual conflicts.
 * Processes milestones from left to right, assigning each to the lowest available level
 * where it won't overlap with existing milestones.
 * 
 * @param milestones - Array of milestone objects with position and display properties
 * @param config - Layout configuration for overlap detection and spacing
 * @returns Array of milestones with assigned vertical levels
 */
export const calculateMilestoneLevels = (
  milestones: Array<ProcessedMilestone>,
  config: MilestoneLayoutConfig = createMilestoneLayoutConfig()
): MilestoneWithLevel[] => {
  const milestonesWithLevels: MilestoneWithLevel[] = [];
  
  // Sort milestones by position to process them left to right
  const sortedMilestones = [...milestones].sort((a, b) => a.position - b.position);
  
  sortedMilestones.forEach((milestone) => {
    let level = 0;
    let hasOverlap = true;
    
    // Find the first available level where this milestone doesn't overlap
    while (hasOverlap) {
      hasOverlap = milestonesWithLevels.some(existing => 
        existing.level === level && 
        Math.abs(existing.position - milestone.position) < config.overlapThreshold
      );
      
      if (hasOverlap) {
        level++;
      }
    }
        
    milestonesWithLevels.push({ 
      ...milestone, 
      name: milestone.name || 'Unnamed Milestone',
      timestamp: milestone.timestamp,
      level 
    });
  });
  
  return milestonesWithLevels;
};
/**
 * Calculates the required height for milestone container based on level distribution.
 * Determines the vertical space needed to accommodate all milestone levels with proper spacing.
 * 
 * @param milestonesWithLevels - Array of milestones with assigned levels
 * @param config - Layout configuration with minimum height and level spacing
 * @returns Total height in pixels required for the milestone container
 */
export const calculateMilestoneContainerHeight = (
  milestonesWithLevels: MilestoneWithLevel[],
  config: MilestoneLayoutConfig = createMilestoneLayoutConfig()
): number => {
  const maxLevel = Math.max(0, ...milestonesWithLevels.map(m => m.level));
  return Math.max(config.minHeight, config.minHeight + maxLevel * config.levelSpacing);
};

/**
 * Smart positioning configuration interface
 */
interface SmartPositioningConfig {
  /** Threshold percentage for detecting left edge proximity */
  leftEdgeThreshold?: number;
  /** Threshold percentage for detecting right edge proximity */
  rightEdgeThreshold?: number;
}

/**
 * Smart positioning result interface
 */
interface SmartPositioningResult {
  /** Base transform for normal state */
  baseTransform: string;
  /** Transform for hover state */
  hoverTransform: string;
  /** Whether element is near left edge */
  isNearLeftEdge: boolean;
  /** Whether element is near right edge */
  isNearRightEdge: boolean;
}

/**
 * Calculates smart positioning transforms based on element position to prevent cutoff at screen edges.
 * For elements near the left edge, aligns them to the right of their vertical line.
 * For elements near the right edge, aligns them to the left of their vertical line.
 * 
 * @param positionPercent - Horizontal position as percentage (0-100)
 * @param config - Configuration for edge detection thresholds
 * @returns Object with base and hover transforms plus edge detection flags
 */
export const calculateSmartPositioning = (
  positionPercent: number,
  config: SmartPositioningConfig = {}
): SmartPositioningResult => {
  const {
    leftEdgeThreshold = 10,
    rightEdgeThreshold = 90
  } = config;
  
  // Determine if element is near edges
  const isNearLeftEdge = positionPercent < leftEdgeThreshold;
  const isNearRightEdge = positionPercent > rightEdgeThreshold;
  
  let baseTransform = 'translateX(-50%)';
  let hoverTransform = 'translateX(-50%) translateY(-8px) scale(1.05)';
  
  if (isNearLeftEdge) {
    // Align to right of vertical line for start elements
    baseTransform = 'translateX(-5%)';
    hoverTransform = 'translateX(5%) translateY(-8px) scale(1.05)';
  } else if (isNearRightEdge) {
    // Align to left of vertical line for end elements
    baseTransform = 'translateX(-90%)';
    hoverTransform = 'translateX(-90%) translateY(-8px) scale(1.05)';
  }
  
  return {
    baseTransform,
    hoverTransform,
    isNearLeftEdge,
    isNearRightEdge
  };
};

/**
 * Calculates smart positioning transforms for time markers with container, paper, and line elements.
 * 
 * @param positionPercent - Horizontal position as percentage (0-100)
 * @param config - Configuration for edge detection thresholds
 * @returns Object with transforms for container, paper, and line elements
 */
export const calculateTimeMarkerSmartPositioning = (
  positionPercent: number,
  config: SmartPositioningConfig = {}
) => {
  const { isNearLeftEdge, isNearRightEdge } = calculateSmartPositioning(positionPercent, config);
  
  let containerTransform = 'translateX(-50%)';
  let paperTransform = 'translateX(-50%)';
  let lineTransform = 'translateX(-50%)';
  
  if (isNearLeftEdge) {
    // Align to right of vertical line for start elements
    containerTransform = 'translateX(0%)';
    paperTransform = 'translateX(0%)';
    lineTransform = 'translateX(0%)';
  } else if (isNearRightEdge) {
    // Align to left of vertical line for end elements
    containerTransform = 'translateX(-100%)';
    paperTransform = 'translateX(-100%)';
    lineTransform = 'translateX(-100%)';
  }
  
  return {
    containerTransform,
    paperTransform,
    lineTransform,
    isNearLeftEdge,
    isNearRightEdge
  };
};

/**
 * Calculates milestone position styling for absolute positioning.
 * Generates CSS properties for positioning milestones at their calculated levels and positions.
 * 
 * @param milestone - Milestone object with position and level information
 * @param config - Layout configuration for base offset and spacing
 * @returns CSS style object with positioning properties
 */
export const calculateMilestonePosition = (
  milestone: MilestoneWithLevel,
  config: MilestoneLayoutConfig = createMilestoneLayoutConfig()
) => ({
  left: `${milestone.position}%`,
  top: `${config.baseTopOffset + milestone.level * config.levelSpacing}px`,
  position: 'absolute' as const,
  transform: 'translateX(-50%)',
  zIndex: LAYOUT_CONSTANTS.BASE_Z_INDEX + milestone.level,
});
